import { ReactElement, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Divider,
  List,
  ListItemButton,
  Paper,
  Typography,
} from '@mui/material';
import { IOrganisationEventItem, IOrganisationEventsResponse } from '../../types/@organisation';

interface IProps {
  events: IOrganisationEventsResponse;
}

const getEventCardStyles = (status: IOrganisationEventItem['status']) => {
  if (status === 'live') {
    return {
      background: '#eaf4ff',
      border: '1px solid #9bc7ff',
      accent: '#1565c0',
      text: '#0e3d73',
    };
  }

  if (status === 'finished') {
    return {
      background: '#f1f3f5',
      border: '1px solid #d7dce1',
      accent: '#6c757d',
      text: '#4f5b66',
    };
  }

  return {
    background: '#ffffff',
    border: '1px solid #e3e8ef',
    accent: '#8c99a5',
    text: '#17324d',
  };
};

const formatDate = (value: number | null): string => {
  if (!value) return 'Start time not set';

  try {
    return new Date(value).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Start time unavailable';
  }
};

const OrganisationEventsPanel = ({ events }: IProps): ReactElement => {
  const [selectedTeamId, setSelectedTeamId] = useState<string>(events.teams[0]?.teamId || '');

  const selectedTeam = useMemo(() => {
    return events.teams.find((team) => team.teamId === selectedTeamId) || events.teams[0] || null;
  }, [events.teams, selectedTeamId]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#17324d' }}>
          Events
        </Typography>
        <Chip label={`Teams: ${events.summary.teamCount}`} />
        <Chip label={`Live: ${events.summary.liveEvents}`} color="primary" />
        <Chip label={`Upcoming: ${events.summary.upcomingEvents}`} />
        <Chip label={`Finished: ${events.summary.finishedEvents}`} />
      </Box>

      {!events.teams.length ? (
        <Alert severity="info">No organisation events are currently available.</Alert>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
            gap: 3,
            alignItems: 'start',
          }}
        >
          <Paper elevation={0} variant="outlined" sx={{ borderRadius: '16px', overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 2, background: '#fafbfd' }}>
              <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#17324d' }}>
                Teams
              </Typography>
              <Typography sx={{ fontSize: '13px', color: '#6b7c8f', mt: 0.5 }}>
                Select a team to see its event list
              </Typography>
            </Box>

            <Divider />

            <List disablePadding>
              {events.teams.map((team) => {
                const active = selectedTeam?.teamId === team.teamId;

                return (
                  <ListItemButton
                    key={team.teamId}
                    selected={active}
                    onClick={() => setSelectedTeamId(team.teamId)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      gap: 1,
                      px: 2,
                      py: 1.75,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <Typography sx={{ fontWeight: 700, color: '#17324d' }}>{team.teamName}</Typography>
                      <Chip size="small" label={team.events.length} />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {team.counts.live > 0 && <Chip size="small" color="primary" label={`Live ${team.counts.live}`} />}
                      {team.counts.upcoming > 0 && <Chip size="small" label={`Upcoming ${team.counts.upcoming}`} />}
                      {team.counts.finished > 0 && <Chip size="small" label={`Finished ${team.counts.finished}`} />}
                    </Box>
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {selectedTeam ? (
              <>
                <Box>
                  <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#17324d' }}>
                    {selectedTeam.teamName}
                  </Typography>
                  <Typography sx={{ fontSize: '14px', color: '#6b7c8f', mt: 0.5 }}>
                    Events are ordered live first, then upcoming, then finished.
                  </Typography>
                </Box>

                {selectedTeam.events.length ? (
                  selectedTeam.events.map((event) => {
                    const palette = getEventCardStyles(event.status);

                    return (
                      <Paper
                        key={event.eventId}
                        elevation={0}
                        sx={{
                          p: 2.25,
                          borderRadius: '16px',
                          background: palette.background,
                          border: palette.border,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.25,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', md: 'center' },
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: 1.25,
                          }}
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: palette.text }}>
                              {event.name}
                            </Typography>
                            {event.slogan ? (
                              <Typography sx={{ fontSize: '14px', color: '#5f7285' }}>{event.slogan}</Typography>
                            ) : null}
                          </Box>

                          <Chip
                            label={event.statusLabel}
                            sx={{
                              background: palette.accent,
                              color: '#fff',
                              fontWeight: 700,
                            }}
                          />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {event.gameType ? <Chip size="small" label={event.gameType} /> : null}
                          <Chip size="small" label={`Period ${event.gamePeriod}/${event.maxGamePeriods}`} />
                          <Chip size="small" label={formatDate(event.eventStartTime)} />
                          <Chip size="small" label={event.source.toUpperCase()} />
                        </Box>

                        {(event.twitterId || event.instagramId) && (
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {event.twitterId ? <Chip size="small" variant="outlined" label={`X ${event.twitterId}`} /> : null}
                            {event.instagramId ? (
                              <Chip size="small" variant="outlined" label={`IG ${event.instagramId}`} />
                            ) : null}
                          </Box>
                        )}
                      </Paper>
                    );
                  })
                ) : (
                  <Alert severity="info">No events are linked to this team yet.</Alert>
                )}
              </>
            ) : (
              <Alert severity="info">Select a team to view its events.</Alert>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default OrganisationEventsPanel;